import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Truck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import VehiculoForm from './VehiculoForm';
import apiService from '../../services/apiService';

const VehiculosTab = () => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [vehiculos, setVehiculos] = useState([]);
    const [filteredVehiculos, setFilteredVehiculos] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedVehiculo, setSelectedVehiculo] = useState(null);

    useEffect(() => {
        loadVehiculos();
    }, []);

    useEffect(() => {
        filterVehiculos();
    }, [vehiculos, searchQuery]);

    const loadVehiculos = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getVehiculos();

            if (result.success) {
                setVehiculos(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar vehículos');
        } finally {
            setLoading(false);
        }
    };

    const filterVehiculos = () => {
        if (!searchQuery.trim()) {
            setFilteredVehiculos(vehiculos);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = vehiculos.filter(vehiculo =>
            vehiculo.nombre.toLowerCase().includes(query) ||
            vehiculo.placa.toLowerCase().includes(query)
        );

        setFilteredVehiculos(filtered);
    };

    const handleCreateVehiculo = async (vehiculoData) => {
        try {
            const result = await apiService.crearVehiculo(vehiculoData);

            if (result.success) {
                setShowCreateModal(false);
                loadVehiculos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear vehículo');
        }
    };

    const handleEditVehiculo = async (vehiculoData) => {
        try {
            const result = await apiService.actualizarVehiculo({
                id: selectedVehiculo.id,
                ...vehiculoData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedVehiculo(null);
                loadVehiculos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar vehículo');
        }
    };

    const handleDeleteVehiculo = async (id, placa) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el vehículo "${placa}"?`)) {
            return;
        }

        try {
            const result = await apiService.eliminarVehiculo(id);

            if (result.success) {
                loadVehiculos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar vehículo');
        }
    };


    return (
        <div className="vehiculos-tab">
            {/* Controles */}
            <div className="vehiculos-controls">
                <div className="search-container">
                    <Input
                        placeholder="Buscar por nombre o placa..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={<Search size={20} />}
                        className="search-input"
                    />
                </div>

                <div className="controls-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        disabled={loading}
                    >

                        Nuevo Vehículo
                    </Button>
                </div>
            </div>

            {/* Información de resumen */}
            <div className="vehiculos-summary">
                <span className="vehiculos-count">
                    {filteredVehiculos.length} de {vehiculos.length} vehículos
                </span>
            </div>

            {/* Tabla de vehículos */}
            <div className="vehiculos-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando vehículos...</p>
                    </div>
                )}

                {!loading && filteredVehiculos.length === 0 && (
                    <div className="empty-state">
                        <Truck size={48} />
                        <h3>No se encontraron vehículos</h3>
                        <p>
                            {searchQuery
                                ? 'No hay vehículos que coincidan con tu búsqueda'
                                : 'No hay vehículos registrados en el sistema'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Registrar primer vehículo
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredVehiculos.length > 0 && (
                    <table className="vehiculos-table-compact">
                        <thead>
                            <tr>
                                <th width="40%">Vehículo</th>
                                <th width="30%">Placa</th>
                                <th width="15%">Estado</th>
                                <th width="15%" data-type="actions">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVehiculos.map((vehiculo) => (
                                <tr key={vehiculo.id} className={vehiculo.activo ? '' : 'inactive-row'}>
                                    <td>
                                        <div className="vehiculo-compact">
                                            <div className="vehiculo-icon">
                                                <Truck size={16} />
                                            </div>
                                            <div className="vehiculo-info">
                                                <div className="vehiculo-nombre">{vehiculo.nombre}</div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="placa-info">
                                            <strong>{vehiculo.placa}</strong>
                                        </div>
                                    </td>

                                    <td>
                                        <span className={`status-dot ${vehiculo.activo ? 'active' : 'inactive'}`}>
                                            {vehiculo.activo ? '● Activo' : '● Inactivo'}
                                        </span>
                                    </td>

                                    <td>
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedVehiculo(vehiculo);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteVehiculo(vehiculo.id, vehiculo.placa)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Registrar Nuevo Vehículo"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <VehiculoForm
                        onSubmit={handleCreateVehiculo}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedVehiculo && (
                <Modal
                    title="Editar Vehículo"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedVehiculo(null);
                    }}
                    size="large"
                >
                    <VehiculoForm
                        vehiculo={selectedVehiculo}
                        onSubmit={handleEditVehiculo}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedVehiculo(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}
        </div>
    );
};

export default VehiculosTab;